import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, iif, Observable, of, Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { switchMap, tap } from 'rxjs/operators';
import { LoaderStatus } from 'libs/core/src/lib/model/loading/loading-status.enum';
import { convertStringToEnumValue, ENVIRONMENT_TOKEN } from 'libs/core/src/public-api';
import { appProjectName } from 'libs/core/src/app.const';
import { BasketDataProvider } from 'libs/core/src/lib/data-provider/basket.data-provider';
import { OrderDataProvider } from 'libs/core/src/lib/data-provider/order.data-provider';
import { UserDataProvider } from 'libs/core/src/lib/data-provider/user.data-provider';
import { MessageModel, MessageType } from 'libs/core/src/lib/model/message.model';
import { BasketPageModel } from 'libs/core/src/lib/model/page/basket/basket.page.model';
import { CateringAggregationArticleViewModel } from 'libs/core/src/lib/model/catering/catering-aggregation.view.model';
import { PersonalModel } from 'libs/core/src/lib/model/personal.model';
import { OrderViewModel } from 'libs/core/src/lib/model/view-model/order/order.view.model';
import { VoucherTypeModel } from 'libs/core/src/lib/model/voucher-type.model';
import { AppService } from 'libs/core/src/lib/service/app.service';
import { BackgroundService } from 'libs/core/src/lib/service/background.service';
import { CateringService } from 'libs/core/src/lib/service/catering/catering.service';
import { CountdownComponentService } from 'libs/core/src/lib/service/countdown.service';
import { MessageService } from 'libs/core/src/lib/service/message.service';
import { NavigationHelperService } from 'libs/core/src/lib/service/navigation/navigation-helper.service';
import { ResponseValidatorService } from 'libs/core/src/lib/service/validator/response-validator.service';
import { VoucherService } from 'libs/core/src/lib/service/voucher.service';
import { AuthStateService } from 'libs/core/src/lib/state/auth.state.service';
import { CateringStateService } from 'libs/core/src/lib/state/catering.state.service';
import { OrderStateService } from 'libs/core/src/lib/state/order.state.service';
import { BlockPaymentModel } from './model/block-payment-model';
import { BlockPaymentEnum } from './model/block-payment.enum';
import { PaymentErrorCode } from './model/payment-error-code.enum';
import { PaymentProviderStateService } from '../../component/payment/service/payment-provider-state.service';
import { PaymentProxyComponent } from '../../component/payment/proxy/payment-proxy.component';
import { OrderPaymentPackage } from '../../../../../core/src/lib/model/order-payment.package.model';
import { DonePaymentProviderEvent, ErrorPaymentKeyProviderEvent } from '../../component/payment/proxy/event/payment-provider.event';
import { ErrorPaymentProviderEvent } from '../../component/payment/proxy/event/payment-provider.event';
import { PaymentProviderEvent } from '../../component/payment/proxy/event/payment-provider.event';
import { WaitPaymentProviderEvent } from '../../component/payment/proxy/event/payment-provider.event';
import { WorkPaymentProviderEvent } from '../../component/payment/proxy/event/payment-provider.event';
import { PaymentMethodViewModel } from 'libs/core/src/lib/model/view-model/order/payment-method/payment-method.view.model';
import { PaymentViewModel } from 'libs/core/src/lib/model/view-model/payment.view.model';
import { GoogleTagManagerService } from 'libs/core/src/lib/service/analytics-services/google-tag-manager.service';
import { LoaderService } from 'libs/core/src/lib/service/loading.service';
import { LoaderEnum } from 'libs/core/src/lib/enum/loader.enum';
import { NavigationService } from 'libs/core/src/lib/service/navigation/navigation.service';
import { OrderHttpService } from 'libs/core/src/lib/http/order.http.service';
import { StateService } from 'libs/core/src/lib/state/state.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ProviderEnum } from 'libs/core/src/lib/enum/provider.enum';
import { OrderApiModel } from 'libs/core/src/lib/model/api-model/order/order.api.model';
import { FlowService } from 'libs/core/src/lib/service/flow.service';
import { IOrderPaymentPackage } from 'libs/core/src/lib/model/interface/order-payment.package.model';
import { ScreenStateService } from 'libs/core/src/lib/state/screen.state.service';
import { ErrorType } from '../error/model/error-type.model';

export interface PaymentPageComponentInterface {}

@UntilDestroy()
@Component({
  template: ''
})
export class PaymentPageComponent implements OnInit, OnDestroy {
  loaderEnum: typeof LoaderEnum = LoaderEnum;
  private orderStateLoaded = false;
  private paymentProviderDone = false;

  public constructor(
    @Inject(ENVIRONMENT_TOKEN) protected environment: any,
    protected orderDataProvider: OrderDataProvider,
    protected userDataProvider: UserDataProvider,
    protected authStateService: AuthStateService,
    protected orderStateService: OrderStateService,
    protected router: Router,
    protected navigationHelperService: NavigationHelperService,
    protected route: ActivatedRoute,
    protected backgroundService: BackgroundService,
    protected messageService: MessageService,
    protected translateService: TranslateService,
    protected responseValidatorService: ResponseValidatorService,
    protected basketDataProvider: BasketDataProvider,
    protected cateringService: CateringService,
    protected voucherService: VoucherService,
    protected paymentProviderStateService: PaymentProviderStateService,
    protected appService: AppService,
    protected countdownComponentService: CountdownComponentService,
    protected cateringStateService: CateringStateService,
    protected screenStateService: ScreenStateService,
    protected googleTagManagerService: GoogleTagManagerService,
    protected loaderService: LoaderService,
    protected navigationService: NavigationService,
    protected orderHttpService: OrderHttpService,
    protected stateService: StateService,
    protected flowService: FlowService
  ) {
    this.cateringStateService.state$.pipe(untilDestroyed(this)).subscribe((value) => {
      this.cateringSaleEnabled = value;
    });

    if (this.route.snapshot.queryParams['sandboxPayment'] && this.route.snapshot.queryParams['sandboxPayment'] === 'true') {
      this.appService.enablePaymentSandbox();
    }

    this.paymentChannel = environment.constants.paymentChannel;
    this.autoSelectedPaymentMethod = environment.constants.autoSelectPaymentMethod;
    this.allowedPaymentMethodList = environment.constants.allowedPaymentMethod;

    if (this.appService.paymentSandboxEnabled) {
      this.allowedPaymentMethodList.push('sandbox');
    }

    this.voucherSaleEnabled = environment.constants.voucherSaleEnabled;
    this.paymentProviderStateService.state$.pipe(untilDestroyed(this)).subscribe((state) => {
      this.onChangeSelectedPaymentMethod(this.paymentMethodList.find((y) => y.identifier === state?.provider));
    });
    this.countdownComponentService.start();
  }

  get isLoaded() {
    return this.loaderService.getLoaderStatus() === LoaderStatus.stopped && this.paymentLoaderStatus === LoaderStatus.stopped;
  }

  @ViewChild('paymentProxyComponent') public paymentProxyComponent: PaymentProxyComponent;
  @ViewChild('buttonElement') public submitButton: ElementRef<HTMLButtonElement>;
  @ViewChild('personalComponent') public personalForm: PaymentPageComponentInterface;
  @ViewChild('feeAgreementCheckbox') public _feeAgreementCheckbox: ElementRef;

  private orderFetchStatus = new Subject<boolean>();
  public orderDataFetched$ = this.orderFetchStatus.asObservable();

  public feeAgreementCheckboxError = false;
  public formIsEditing = true;
  public paymentLoaderStatus = LoaderStatus.pending;
  /**
   * The list of available payment method for order
   */
  public paymentMethodList: Array<PaymentMethodViewModel> = new Array<PaymentMethodViewModel>();

  /**
   * The payment data received from payment provider after init
   */
  public paymentModel: PaymentViewModel = null;

  /**
   * Selected payment method
   */
  public selectedPaymentMethod: PaymentMethodViewModel = null;
  public selectedCateringArticleCombinationList: Array<CateringAggregationArticleViewModel> = null;
  public selectedVoucherList: Array<VoucherTypeModel> = null;
  public basketPageModel: BasketPageModel = new BasketPageModel();
  public personalComponentFormErrors: Array<string> | null = null;
  public showPaymentBlock = false;
  public personalFormData: PersonalModel = null;
  public paymentErrorMessage: string = null;

  /**
   * The payment channel that should be used
   */
  private readonly paymentChannel: string | null = null;

  /**
   * The default payment method to auto select
   */
  private readonly autoSelectedPaymentMethod: string | null = null;

  /**
   * The list of allowed payment methods. If null the constraint is disable
   */
  private readonly allowedPaymentMethodList: Array<string> | null = null;

  private readonly voucherSaleEnabled: boolean = false;
  private cateringSaleEnabled = false;
  public isPaymentInitiating = false;
  public providerEnum: typeof ProviderEnum = ProviderEnum;

  public embededPaymentActive = false;

  public order: OrderViewModel;

  public ngOnInit() {
    this.backgroundService.changeDeleteOrderOnWindowUnloadState(false);
    this.loaderService.showLoader(LoaderEnum.MAIN);
    this.loadOrderState();
    this.cateringStateService.checkAvailability();
    this.googleTagManagerService.addToCart();
    this.googleTagManagerService.beginCheckout();
  }

  public ngOnDestroy(): void {
    this.paymentProviderStateService.clearState();
  }

  private tryHideLoader(): void {
    if (this.orderStateLoaded && this.paymentProviderDone) {
      this.loaderService.hideLoader(LoaderEnum.MAIN);
      this.paymentLoaderStatus = LoaderStatus.stopped;
    }
  }

  public blockPayment(value: BlockPaymentModel): void {
    switch (value.id) {
      case BlockPaymentEnum.feeAgreement:
        this.feeAgreementCheckboxError = value.value;
        break;
      case BlockPaymentEnum.editPersonalData:
        this.formIsEditing = value.value;
        if (this._feeAgreementCheckbox?.nativeElement) {
          this._feeAgreementCheckbox.nativeElement.disabled = !value.value;
        }
        break;
    }
  }

  public payButtonClicked(): void {
    console.log('PAYYY');
    if (this.canCompleteOrder()) {
      this.loaderService.showLoader(LoaderEnum.MAIN);
      this.orderDataProvider.closeBasket(this.order.cinemaId, this.order.id).subscribe({
        next: () => {},
        error: () => {},
        complete: () => {
          const nextRoute = this.navigationHelperService.getNextRouteFor(this.route.snapshot.data.pageIdentify);
          this.navigationService.goTo([nextRoute], {
            queryParams: this.getQueryParams()
          });
        }
      });
    } else {
      if (
        (this.appService.isProject(appProjectName.SCHULMAN, appProjectName.VIOLET_CROWN, appProjectName.LANDMARK) && this.formIsEditing) ||
        !this.selectedPaymentMethod?.identifier
      ) {
        return;
      }

      if (this.selectedPaymentMethod?.identifier === ProviderEnum.WORLDPAY) {
        this.embededPaymentActive = true;
      }

      if (this.canCompleteOrder()) {
        this.loaderService.showLoader(LoaderEnum.MAIN);
        this.orderDataProvider.closeBasket(this.order.cinemaId, this.order.id).subscribe({
          next: () => {},
          error: () => {},
          complete: () => {
            const nextRoute = this.navigationHelperService.getNextRouteFor(this.route.snapshot.data.pageIdentify);
            this.navigationService.goTo([nextRoute], {
              queryParams: this.getQueryParams()
            });
          }
        });
      } else {
        this.isPaymentInitiating = true;

        const preInitPaymentEvent: OrderPaymentPackage = new OrderPaymentPackage();
        preInitPaymentEvent.continueUrl = this.getContinueUrl();
        preInitPaymentEvent.paymentProviderIdentifier = this.selectedPaymentMethod.identifier;
        this.paymentProxyComponent.onPreInitPayment(preInitPaymentEvent).subscribe((x) => {
          if (x.abort === true) {
            this.isPaymentInitiating = false;
            return;
          }

          this.orderStateService.setPaymentMethod(this.selectedPaymentMethod);
          this.googleTagManagerService.addPaymentInfo(this.selectedPaymentMethod.name);
          this.initPayment(this.selectedPaymentMethod.identifier, x);
        });
      }
    }
  }

  public onPersonalFormEvent(personalData: PersonalModel): void {
    if (personalData) {
      this.personalFormData = personalData;

      const apiModel: OrderApiModel = this.order.toApiModel();
      apiModel.userEmail = this.personalFormData.email;
      apiModel.userFirstName = this.personalFormData.firstName;
      apiModel.userLastName = this.personalFormData.lastName;
      apiModel.userPhone = this.personalFormData.phone;
      apiModel.taxId = this.personalFormData.taxId;

      this.orderDataProvider.update(this.order.cinemaId, apiModel).subscribe();
      this.showPaymentBlock = true;
    }
  }

  public onChangeSelectedPaymentMethod(selectedPaymentMethod: PaymentMethodViewModel): void {
    this.selectedPaymentMethod = selectedPaymentMethod;

    if (this.appService.isProject(appProjectName.CINEPOLIS) && this.selectedPaymentMethod?.identifier === ProviderEnum.WORLDPAY) {
      setTimeout(() => {
        this.payButtonClicked();
      });
    }
  }

  public onPaymentProviderEvent(event: PaymentProviderEvent): void {
    if (event instanceof WaitPaymentProviderEvent) {
      this.loaderService.showLoader(LoaderEnum.MAIN);
      this.paymentLoaderStatus = LoaderStatus.pending;
    }

    if (event instanceof WorkPaymentProviderEvent) {
      this.loaderService.showLoader(LoaderEnum.MAIN);
      this.paymentLoaderStatus = LoaderStatus.pending;
    }

    if (event instanceof DonePaymentProviderEvent) {
      this.paymentProviderDone = true;
      this.tryHideLoader();
    }

    if (event instanceof ErrorPaymentProviderEvent) {
      this.loaderService.hideLoader(LoaderEnum.MAIN);
      this.paymentLoaderStatus = LoaderStatus.stopped;
      this.errorHandler(event.originError as HttpErrorResponse);
    }

    if (event instanceof ErrorPaymentKeyProviderEvent) {
      this.loaderService.hideLoader(LoaderEnum.MAIN);
      this.paymentLoaderStatus = LoaderStatus.stopped;
      this.showError(event.key);
    }
  }

  private loadOrderState(): void {
    const providerEnumValue = convertStringToEnumValue(ProviderEnum, this.autoSelectedPaymentMethod);
    const deletePayments$ = (order) => {
      return this.orderStateService.hasExternalPayments() ? this.orderStateService.clearExternalPayments(providerEnumValue) : of(order);
    };

    this.orderStateService.state$
      .pipe(
        switchMap((order) =>
          this.orderDataProvider.getPaymentMethodList(order.cinemaId, order.id).pipe(
            tap({
              next: (paymentMethodList: Array<PaymentMethodViewModel>) => {
                if (this.appService.isProject(appProjectName.CINEPOLIS)) {
                  this.changeWorldPayName(paymentMethodList);
                }

                if (!this.paymentMethodList?.length) {
                  this.paymentMethodList = this.filterPaymentMethodList(paymentMethodList);
                  this.selectDefaultPaymentMethod();
                }

                if (
                  this.autoSelectedPaymentMethod === null ||
                  this.autoSelectedPaymentMethod === ProviderEnum.WORLDPAY ||
                  this.appService.isProject(appProjectName.HELIOS) ||
                  this.appService.isProject(appProjectName.CINEMA1)
                ) {
                } else {
                  const preInitPaymentEvent: OrderPaymentPackage = new OrderPaymentPackage();
                  preInitPaymentEvent.continueUrl = this.getContinueUrl();

                  this.initPayment(this.autoSelectedPaymentMethod, preInitPaymentEvent);
                }
              },
              error: (e) => {
                this.errorHandler(e);
                this.setOrderStateLoaded();
              },
              complete: () => (this.isPaymentInitiating = false)
            }),
            switchMap(() => of(order))
          )
        ),
        switchMap((order) => deletePayments$(order)),
        tap((order) => this.setOrder(order)),
        tap(() => this.orderFetchStatus.next(true)),
        switchMap((order) =>
          this.basketDataProvider.updateBasket(order).pipe(
            tap({
              next: (basketPageModel) => {
                this.basketPageModel = basketPageModel;
                this.screenStateService.setData(this.basketPageModel.screen);
                this.setOrderStateLoaded();
              },
              error: (e) => {
                this.errorHandler(e);
                this.setOrderStateLoaded();
              }
            })
          )
        ),
        untilDestroyed(this)
      )
      .subscribe({
        error: (e) => {
          this.errorHandler(e);
          this.setOrderStateLoaded();
        }
      });
  }

  setOrderStateLoaded() {
    this.orderStateLoaded = true;
    this.tryHideLoader();
  }

  setOrder(order) {
    this.order = order;
  }

  changeWorldPayName(paymentMethodList: PaymentMethodViewModel[]) {
    if (paymentMethodList.filter((p) => p.identifier == ProviderEnum.WORLDPAY).length > 0) {
      paymentMethodList.filter((p) => p.identifier == ProviderEnum.WORLDPAY)[0].name = this.translateService.instant('payment.creditCard.title');
    }
  }

  public getContinueUrl(): string {
    return this.generateContinueUrl();
  }

  private initPayment(paymentProviderIdentifier: string, preInitPaymentEvent: OrderPaymentPackage): void {
    this.loaderService.showLoader(LoaderEnum.MAIN);
    this.isPaymentInitiating = true;

    const proxyOnPostInitPayment = (paymentModel, paymentProviderIdentifier) => {
      this.paymentProxyComponent.onPostInitPayment(paymentModel, paymentProviderIdentifier);
    };

    this.orderDataProvider
      .postPayment(this.createPaymentPackage(paymentProviderIdentifier, preInitPaymentEvent))
      .pipe(
        tap({
          next: (paymentModel: PaymentViewModel) => {
            proxyOnPostInitPayment(paymentModel, paymentProviderIdentifier);
          },
          error: (e) => this.errorHandler(e),
          complete: () => (this.isPaymentInitiating = false)
        }),
        switchMap(() => {
          const order = this.orderStateService.getOrder();
          return this.orderDataProvider.getOrder(order?.cinemaId, order?.id);
        })
      )
      .subscribe({
        next: (o) => {
          this.orderStateService.setOrder(o);
        },
        error: () => {}
      });
  }

  private createPaymentPackage(paymentProviderIdentifier: string, orderPaymentPackage: OrderPaymentPackage): IOrderPaymentPackage {
    orderPaymentPackage = Object.assign(new OrderPaymentPackage(), orderPaymentPackage);
    orderPaymentPackage.cinemaId = this.order.cinemaId;
    orderPaymentPackage.orderId = this.order.id;
    orderPaymentPackage.channel = this.paymentChannel;
    orderPaymentPackage.paymentProviderIdentifier = paymentProviderIdentifier;

    return orderPaymentPackage;
  }

  protected generateContinueUrl(): string {
    let urlTree: string = '';
    if (this.autoSelectedPaymentMethod === ProviderEnum.WORLDPAY) {
      urlTree = this.router
        .createUrlTree(['p'], {
          queryParams: this.getQueryParams()
        })
        .toString();
    } else {
      urlTree = this.router
        .createUrlTree([this.navigationHelperService.getNextRoute(this.route.snapshot)], {
          queryParams: this.getQueryParams()
        })
        .toString();
    }

    return `${window.location.origin}/#${urlTree}`;
  }

  private getQueryParams() {
    return {
      orderId: this.order?.id,
      cinemaId: this.order?.cinemaId
    };
  }

  private filterPaymentMethodList(paymentMethodList: Array<PaymentMethodViewModel>): Array<PaymentMethodViewModel> {
    if (this.allowedPaymentMethodList === null) {
      return paymentMethodList;
    }

    return paymentMethodList
      .filter((paymentMethod: PaymentMethodViewModel) => {
        if (paymentMethod.identifier) {
          return this.allowedPaymentMethodList.indexOf(paymentMethod.identifier.toLowerCase()) >= 0;
        }
      })
      .map((o) => {
        o.disabled = this.selectedPaymentMethod?.identifier === o.identifier;
        return o;
      })
      .sort((a, b) => {
        return a.identifier
          ? this.allowedPaymentMethodList.indexOf(a.identifier.toLowerCase()) < this.allowedPaymentMethodList.indexOf(b.identifier.toLowerCase())
            ? -1
            : 1
          : 1;
      });
  }

  private selectDefaultPaymentMethod(): void {
    if (this.appService.isProject(appProjectName.CINEPOLIS)) {
      return;
    }

    if (!this.selectedPaymentMethod && this.autoSelectedPaymentMethod && !this.appService.isProject(appProjectName.HELIOS)) {
      this.selectedPaymentMethod = this.paymentMethodList.find((o) => o.identifier === this.autoSelectedPaymentMethod);
    }
  }

  private showError(code?: string): void {
    if (!code) {
      return;
    }

    //code = code ? code : '30005'; // code or default
    this.messageService.add(new MessageModel(MessageType.danger, this.translateService.instant(`errors.${code}`)));
  }

  /**
   * An callback which handles errors from requests
   */
  private errorHandler(err: HttpErrorResponse): void {
    const errStatus: any = err && err.status ? err.status : null;

    if (errStatus === 500) {
      this.navigationHelperService.redirectToError(ErrorType.unexpectedErrorOccurred);
    } else if (errStatus === 400) {
      const isHandled: boolean = this.handleValidationError(err);

      if (isHandled === false) {
        this.loaderService.hideLoader(LoaderEnum.MAIN);
        this.paymentLoaderStatus = LoaderStatus.stopped;
        this.isPaymentInitiating = false;
        return this.responseValidatorService.viewErrors(err);
      }
    } else if (errStatus === 404) {
      this.navigationHelperService.redirectToError(ErrorType.paymentAlreadyStarted);
    }

    this.responseValidatorService.viewErrors(err);
    this.loaderService.hideLoader(LoaderEnum.MAIN);
    this.paymentLoaderStatus = LoaderStatus.stopped;
    this.isPaymentInitiating = false;
  }

  /**
   * Handles an well described errors
   */
  private handleValidationError(err: HttpErrorResponse): boolean {
    let infoCode: string = null;

    if (err && err.error && err.error.error) {
      const e = err.error.error;
      if (e.info_code) {
        infoCode = e.info_code;
      } else if (e.code) {
        infoCode = e.code;
      }
    }

    if (infoCode && typeof infoCode !== 'string') {
      infoCode = (infoCode as any).toString();
    }

    let translationResult = false;
    const numericInfoCode = Number.parseInt(infoCode);

    if (numericInfoCode === PaymentErrorCode.missedBuyerEmailAddress) {
      this.goToPreviousRoute();
    }

    if (infoCode !== null) {
      const translationKey = `payment.errors.${this.selectedPaymentMethod?.identifier}.${infoCode}`;
      const errorContentTranslation: string = this.translateService.instant(translationKey);

      if (errorContentTranslation !== translationKey) {
        this.messageService.clear();

        if (!this.appService.isProject(appProjectName.SCHULMAN, appProjectName.VIOLET_CROWN, appProjectName.LANDMARK, appProjectName.ONEIL)) {
          this.messageService.add(new MessageModel(MessageType.danger, errorContentTranslation, infoCode));
        } else {
          this.paymentErrorMessage = errorContentTranslation;
        }

        translationResult = true;
      }
    }

    return translationResult;
  }

  private goToPreviousRoute(): void {
    this.navigationService.goTo([this.navigationHelperService.getPreviousRoute(this.route.snapshot)]);
  }

  private loadCateringItemList(): Observable<Array<CateringAggregationArticleViewModel>> {
    if (this.cateringSaleEnabled === false) {
      return of([]);
    }

    return this.cateringService.buildSelectedCateringArticleCombinationList(this.order.cinemaId, this.order, []).pipe(
      tap((combinationList) => {
        this.selectedCateringArticleCombinationList = combinationList;
      })
    );
  }

  private loadVoucherItemList(): Observable<Array<VoucherTypeModel>> {
    if (this.voucherSaleEnabled === false) {
      return of([]);
    }

    this.selectedVoucherList = this.voucherService.createVoucherCollectionFromOrder(this.order);
    return of([]);
  }

  public onNavigationClick(event: string) {
    switch (event) {
      case 'previous':
        this.onPreviousAction();
        break;
      case 'next':
        this.submitButton.nativeElement.click();
        break;
    }
  }

  public onPreviousAction(previousRoute: boolean = true) {
    if (previousRoute) {
      this.navigationService.goTo([this.navigationHelperService.getPreviousRoute(this.route.snapshot)]);
    }
  }

  public onAgreementClick(): void {
    this.feeAgreementCheckboxError = false;
  }

  public onClickedCloseModal(): void {
    this.paymentErrorMessage = null;
  }

  public onStepBackClick(): void {
    this.navigationService.back();
  }

  canCompleteOrder() {
    return this.order?.valueToPay === 0 && this.order?.status === 0;
  }

  clearExternalPayments() {
    this.clearExternalPaymentsObservable(this.order).subscribe();
  }

  clearExternalPaymentsObservable(order: OrderViewModel): Observable<OrderViewModel> {
    return of(order);
  }
}
